import { useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import Icon from "../ui/Icon";
import NewTaskModal from "./NewTaskModal";
import TaskModal from "./TaskModal";
import { BRAND, THEMES } from "../../config/constants";
import { db } from "../../firebase";
import { staffAvatar, btnP } from "../../utils/styles";

export default function WorkflowSection({ currentUser, theme, clients, onNavigateToClient }) {
  const t = THEMES[theme];
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState("mine");
  const [showNew, setShowNew] = useState(false);
  const [openTask, setOpenTask] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "tasks"), snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTasks(all.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)));
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (openTask) {
      const updated = tasks.find(tk => tk.id === openTask.id);
      if (updated) setOpenTask(updated);
    }
  }, [tasks]);

  const myTasks = tasks.filter(tk => tk.assignedTo === currentUser && tk.status !== "closed" && tk.status !== "resolved");
  const sentTasks = tasks.filter(tk => tk.createdBy === currentUser && tk.assignedTo !== currentUser && tk.status !== "closed" && tk.status !== "resolved");
  const allActive = tasks.filter(tk => tk.status !== "closed" && tk.status !== "resolved");
  const resolvedTasks = tasks.filter(tk => tk.status === "resolved" || tk.status === "closed");

  const shown = tab === "mine" ? myTasks : tab === "sent" ? sentTasks : tab === "all" ? allActive : resolvedTasks;

  const statusLabel = (task) => {
    if (task.status === "closed") return { label: "Archived", color: "#6b7280" };
    if (task.status === "resolved") return { label: "Resolved", color: BRAND.blue };
    if (task.status === "completed" && task.assignedTo === currentUser) return { label: "Review & Close", color: BRAND.green };
    if (task.status === "completed") return { label: "Completed", color: BRAND.green };
    if (task.status === "open" && task.assignedTo === currentUser) return { label: "Action needed", color: "#f59e0b" };
    if (task.status === "open") return { label: "In progress", color: t.textMuted };
    return { label: task.status || "Unknown", color: t.textMuted };
  };

  const timeAgo = (ts) => {
    if (!ts) return "";
    const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    const diff = Date.now() - d;
    if (diff < 60000) return "just now";
    if (diff < 3600000) return Math.floor(diff / 60000) + "m ago";
    if (diff < 86400000) return Math.floor(diff / 3600000) + "h ago";
    return Math.floor(diff / 86400000) + "d ago";
  };

  const tabs = [
    ["mine", "My Queue", myTasks.length],
    ["sent", "Sent", sentTasks.length],
    ["all", "All Active", allActive.length],
    ["closed", "Resolved", resolvedTasks.length],
  ];

  return (
    <div style={{ marginBottom: 20 }}>
      {showNew && <NewTaskModal currentUser={currentUser} onClose={() => setShowNew(false)} theme={theme} clients={clients} />}
      {openTask && <TaskModal task={openTask} currentUser={currentUser} onClose={() => setOpenTask(null)} theme={theme} clients={clients} onNavigateToClient={onNavigateToClient} />}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: t.text }}>Workflow</div>
        <div style={{ display: "flex", gap: 1, background: t.bg, border: "1px solid " + t.border, borderRadius: 8, padding: 2 }}>
          {tabs.map(([id, label, count]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: tab === id ? t.surface : "transparent", color: tab === id ? t.text : t.textMuted, fontSize: 12, fontWeight: tab === id ? 700 : 500, cursor: "pointer", fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
              {label}
              {count > 0 && <span style={{ fontSize: 10, fontWeight: 800, background: tab === id ? (id === "closed" ? "#6b7280" : BRAND.blue) : t.border, color: tab === id ? "#fff" : t.textMuted, padding: "1px 6px", borderRadius: 10 }}>{count}</span>}
            </button>
          ))}
        </div>
        <button onClick={() => setShowNew(true)} style={{ ...btnP, padding: "6px 14px", fontSize: 12, marginLeft: "auto" }}>
          <Icon name="plus" size={12} color="#fff" />New Task
        </button>
      </div>

      {/* Task cards — capsule format */}
      {shown.length === 0 ? (
        <div style={{ background: t.surface, border: "1px solid " + t.border, borderRadius: 12, padding: "20px", textAlign: "center", color: t.textMuted, fontSize: 12.5 }}>
          {tab === "mine" ? "🎉 No tasks assigned to you" : tab === "sent" ? "No tasks sent" : tab === "closed" ? "No resolved tasks yet" : "No active tasks"}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {shown.map(task => {
            const sl = statusLabel(task);
            const isCompleted = task.status === "completed" && task.assignedTo === currentUser;
            const isClosed = task.status === "closed" || task.status === "resolved";
            return (
              <div key={task.id} onClick={() => setOpenTask(task)}
                style={{ background: t.surface, border: "1px solid " + (isCompleted ? BRAND.green + "50" : t.border), borderRadius: 12, padding: "12px 16px", cursor: "pointer", minWidth: 220, maxWidth: 280, flex: "1 1 220px", transition: "all 0.12s", boxShadow: isCompleted ? "0 0 0 2px " + BRAND.green + "20" : "none", opacity: isClosed ? 0.55 : 1 }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: sl.color, background: sl.color + "18", padding: "2px 7px", borderRadius: 6 }}>{sl.label}</span>
                  <span style={{ fontSize: 10.5, color: t.textMuted }}>{timeAgo(task.updatedAt)}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 4, lineHeight: 1.3 }}>{task.title}</div>
                {(task.clientName || task.businessName) && <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 6 }}>{task.clientName ? <span onClick={e=>{e.stopPropagation();if(onNavigateToClient&&task.clientId){const c=clients.find(cl=>cl.id===task.clientId);if(c)onNavigateToClient(c);}}} style={{ cursor: task.clientId&&onNavigateToClient?"pointer":"default", color: task.clientId&&onNavigateToClient?BRAND.blue:t.textMuted }}>👤 {task.clientName}</span> : ""}{task.businessName ? (task.clientName ? " · " : "") + "🏢 " + task.businessName : ""}</div>}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: t.textMuted }}>
                    {staffAvatar(task.assignedTo, 16)}
                    <span>{task.assignedTo}</span>
                    {task.createdBy !== task.assignedTo && (
                      <>
                        <span style={{ color: t.border, margin: "0 2px" }}>←</span>
                        {staffAvatar(task.createdBy, 16)}
                        <span>{task.createdBy}</span>
                      </>
                    )}
                  </div>
                  {(task.comments || []).filter(c => c.type !== "system").length > 0 && (
                    <span style={{ fontSize: 10.5, color: t.textMuted }}>💬 {(task.comments || []).filter(c => c.type !== "system").length}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ borderBottom: "1px solid " + t.border, marginTop: 20 }} />
    </div>
  );
}
